import { Dispatch, useMemo, useState, SetStateAction } from 'react';
import { createSafeContext } from '@mews/framework';
import { UserProfileResult } from '@mews-commander/data-access';

interface UserDetailContextValue {
    activeUserDetailId: UserProfileResult['userId'] | null;
    closeUserDetail: () => void;
    activeTab: TabState;
    setActiveTab: Dispatch<SetStateAction<TabState>>;
    openUserDetail: ({ userId, properties }: OpenUserDetailPayload) => void;
}

interface OpenUserDetailPayload {
    userId: UserProfileResult['userId'];
    properties?: boolean;
}

export enum TabState {
    General = 'general',
    Properties = 'properties',
    Devices = 'devices',
}

export const { UserDetailContextProvider: UserDetailSafeContextProvider, useUserDetailContext } = createSafeContext<
    'UserDetailContext',
    UserDetailContextValue
>('UserDetailContext');

export const UserDetailContextProvider: React.FC<React.PropsWithChildren<{}>> = ({ children }) => {
    const [activeUserDetailId, setActiveUserDetailId] = useState<UserProfileResult['userId'] | null>(null);
    const [activeTab, setActiveTab] = useState<TabState>(TabState.General);

    const userDetailContextValue = useMemo(
        () => ({
            activeUserDetailId,
            closeUserDetail: () => setActiveUserDetailId(null),
            openUserDetail: ({ userId, properties }: OpenUserDetailPayload) => {
                setActiveUserDetailId(userId);
                setActiveTab(properties ? TabState.Properties : TabState.General);
            },
            activeTab,
            setActiveTab,
        }),
        [activeUserDetailId, activeTab]
    );

    return <UserDetailSafeContextProvider value={userDetailContextValue}>{children}</UserDetailSafeContextProvider>;
};
